
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { farmPriceService } from '@/services/farmPriceService';
import { FarmPriceFilters, CreateFarmPriceInput } from '@/types/database/farm-prices';
import { toast } from '@/hooks/use-toast';

export const useFarmPrices = (filters?: FarmPriceFilters) => {
  return useQuery({
    queryKey: ['farm-prices', filters],
    queryFn: () => farmPriceService.getFarmPrices(filters),
  });
};

export const useFarmPrice = (id: string) => {
  return useQuery({
    queryKey: ['farm-price', id],
    queryFn: () => farmPriceService.getFarmPrice(id),
    enabled: !!id,
  });
};

export const useCreateFarmPrice = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: farmPriceService.createFarmPrice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['farm-prices'] });
      toast({
        title: 'Success',
        description: 'Farm price created successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to create farm price',
        variant: 'destructive',
      });
      console.error('Error creating farm price:', error);
    },
  });
};

export const useUpdateFarmPrice = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateFarmPriceInput> }) =>
      farmPriceService.updateFarmPrice(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['farm-prices'] });
      queryClient.invalidateQueries({ queryKey: ['farm-price'] });
      toast({
        title: 'Success',
        description: 'Farm price updated successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to update farm price',
        variant: 'destructive',
      });
      console.error('Error updating farm price:', error);
    },
  });
};

export const useDeleteFarmPrice = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: farmPriceService.deleteFarmPrice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['farm-prices'] });
      toast({
        title: 'Success',
        description: 'Farm price deleted successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to delete farm price',
        variant: 'destructive',
      });
      console.error('Error deleting farm price:', error);
    },
  });
};

export const useBulkCreateFarmPrices = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: farmPriceService.bulkCreateFarmPrices,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['farm-prices'] });
      toast({
        title: 'Success',
        description: `${data.length} farm prices uploaded successfully`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to upload farm prices',
        variant: 'destructive',
      });
      console.error('Error bulk creating farm prices:', error);
    },
  });
};
